import { InjectionToken } from '@angular/core';
import { Canvas, type CanvasOptions } from 'fabric';

/**
 * `fabric.Canvas` with a per-instance backing-store multiplier (`canvas-image-quality`).
 *
 * `getRetinaScaling()` is the single seam fabric consults for the backing-store density
 * (`setDimensions`, export, hit-test tolerance). Overriding it PER INSTANCE — instead of the
 * global `config.devicePixelRatio` — keeps every other fabric canvas in the app (PracticeTrainer)
 * on the browser default.
 *
 * Constructor-timing pitfall: fabric's constructor sizes the DOM elements from the GLOBAL
 * device-pixel ratio before subclass fields initialize, so the override alone is not enough —
 * the factory must re-apply the (unchanged) logical size once after setting `pixelRatio`
 * (`setDimensions(…, { backstoreOnly: true })`), which recomputes the backing store through the
 * override. That happens before the binding attaches and before any content renders.
 */
export class SlideCanvas extends Canvas {
    /** Backing-store multiplier over the logical size; undefined = fabric default behavior. */
    pixelRatio?: number;

    override getRetinaScaling(): number {
        return this.pixelRatio ?? super.getRetinaScaling();
    }
}

/**
 * Creates the renderer-owned `fabric.Canvas` over the component's `<canvas>` element,
 * rasterised at the given quality-derived backing-store multiplier (see {@link SlideCanvas}).
 *
 * A DI seam with exactly one purpose beyond that: unit tests override it to hand the renderer a
 * fake canvas (jsdom has no 2D context, and the renderer's pointer/z-band behavior is tested
 * against a recorded-handler fake — see `slide-canvas-renderer.component.spec.ts`).
 */
export type FabricCanvasFactory = (
    element: HTMLCanvasElement,
    options: Partial<CanvasOptions> & { pixelRatio?: number },
) => Canvas;

export const FABRIC_CANVAS_FACTORY = new InjectionToken<FabricCanvasFactory>('FABRIC_CANVAS_FACTORY', {
    providedIn: 'root',
    factory:
        () =>
        (element, { pixelRatio, ...options }) => {
            const canvas = new SlideCanvas(element, options);
            if (pixelRatio !== undefined) {
                canvas.pixelRatio = pixelRatio;
                // Re-apply the unchanged logical size so fabric recomputes the backing store through
                // the override (the constructor used the global DPR — see SlideCanvas docs).
                canvas.setDimensions({ width: canvas.width, height: canvas.height }, { backstoreOnly: true });
            }
            return canvas;
        },
});
